import { createAppSlice } from "@/app/createAppSlice"
import type { PayloadAction } from "@reduxjs/toolkit"
import api from "@/services/api"

// 사용자 정보 타입
type User = {
  id: number
  email: string
  name: string
  nickname: string
  profileImageUrl: string
  provider: string
  role: string
}

// Slice 상태 타입
type AuthState = {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoginSheetOpen: boolean // 로그인 액션 시트 상태
  status: "idle" | "loading" | "failed"
}

// localStorage에서 초기 상태 로드
const accessToken = localStorage.getItem("accessToken")
const refreshToken = localStorage.getItem("refreshToken")
const userItem = localStorage.getItem("user")
const user = userItem ? (JSON.parse(userItem) as User) : null

const initialState: AuthState = {
  user: user,
  accessToken: accessToken,
  refreshToken: refreshToken,
  isAuthenticated: !!accessToken,
  status: "idle",
  isLoginSheetOpen: false, // 로그인 액션 시트 상태
}

export const authSlice = createAppSlice({
  name: "auth",
  initialState,
  reducers: create => ({
    // 로그인 액션 시트 상태 변경
    setLoginSheetOpen: create.reducer(
      (state, action: PayloadAction<boolean>) => {
        state.isLoginSheetOpen = action.payload
      },
    ),
    // 로그인 성공 시 인증 정보 저장
    setCredentials: create.reducer(
      (
        state,
        action: PayloadAction<{
          accessToken: string
          refreshToken: string
          user?: User | null
        }>,
      ) => {
        const { accessToken, refreshToken, user } = action.payload
        state.accessToken = accessToken
        state.refreshToken = refreshToken
        state.isAuthenticated = true
        localStorage.setItem("accessToken", accessToken)
        localStorage.setItem("refreshToken", refreshToken)

        if (user) {
          state.user = user
          localStorage.setItem("user", JSON.stringify(user))
        }
      },
    ),
    // 로그아웃
    logOut: create.reducer(state => {
      state.user = null
      state.accessToken = null
      state.refreshToken = null
      state.isAuthenticated = false
      localStorage.removeItem("accessToken")
      localStorage.removeItem("refreshToken")
      localStorage.removeItem("user")
    }),
    // 비동기로 사용자 정보 가져오기
    fetchUser: create.asyncThunk(
      async () => {
        const response = await api.get<User>("/api/user/me")
        return response.data
      },
      {
        pending: state => {
          state.status = "loading"
        },
        fulfilled: (state, action) => {
          state.status = "idle"
          state.user = action.payload
          state.isAuthenticated = true
          localStorage.setItem("user", JSON.stringify(action.payload))
        },
        rejected: (state) => {
          state.status = "failed"
          // 401 에러에 의한 로그아웃 처리는 axiosSetup의 interceptor에서 담당합니다.
          // 여기서 모든 상태를 지워버리면 Race Condition 발생 시 새로 받아온 토큰까지 지워질 수 있습니다.
        },
      },
    ),
    // 내 프로필 정보 수정
    updateUserProfile: create.asyncThunk(
      async (profileData: { name: string; nickname: string }) => {
        const response = await api.put<User>(
          "/api/user/me/profile",
          profileData,
        )
        return response.data
      },
      {
        pending: state => {
          state.status = "loading"
        },
        fulfilled: (state, action) => {
          state.status = "idle"
          if (state.user) {
            state.user = { ...state.user, ...action.payload }
            localStorage.setItem("user", JSON.stringify(state.user))
          }
        },
        rejected: state => {
          state.status = "failed"
          // TODO: 에러 처리, 예를 들어 원래 상태로 복원하거나 에러 메시지 표시
        },
      },
    ),
    // 내 프로필 이미지 수정
    updateUserProfileImage: create.asyncThunk(
      async (imageFile: File) => {
        const formData = new FormData()
        formData.append("file", imageFile)

        const response = await api.post<User>(
          "/api/user/me/profile-image",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          },
        )
        return response.data
      },
      {
        pending: state => {
          state.status = "loading"
        },
        fulfilled: (state, action) => {
          state.status = "idle"
          if (state.user) {
            state.user.profileImageUrl = action.payload.profileImageUrl
            localStorage.setItem("user", JSON.stringify(state.user))
          }
        },
        rejected: state => {
          state.status = "failed"
        },
      },
    ),
  }),
  // Selector 정의
  selectors: {
    selectIsAuthenticated: auth => auth.isAuthenticated,
    selectCurrentUser: auth => auth.user,
    selectCurrentToken: auth => auth.accessToken,
    selectAuthStatus: auth => auth.status,
    selectIsLoginSheetOpen: auth => auth.isLoginSheetOpen, // 추가
  },
})

// 액션 export
export const {
  setCredentials,
  logOut,
  fetchUser,
  setLoginSheetOpen,
  updateUserProfile,
  updateUserProfileImage,
} = authSlice.actions // setLoginSheetOpen 추가


// Selector export
export const {
  selectIsAuthenticated,
  selectCurrentUser,
  selectCurrentToken,
  selectAuthStatus,
  selectIsLoginSheetOpen,
} = authSlice.selectors // selectIsLoginSheetOpen 추가
